/* ═══════════════════════════════════════════════════════════════
   App — Main application shell (routing, layout, sidebar)
   Depends on: constants.ts, utils.ts, TokenComponents.tsx,
               EditorComponents.tsx, + page components (Home, Projects, etc.)
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { mkDoc, mkHF, mkSp } from './utils';
import { rem } from './lib/rem';
import { getUiScale, subscribeUiScale } from './lib/uiScale';
import { usePref } from './lib/usePref';
import { csiLevelStyles, fillFormaCsiGaps } from '../shared/csi-defaults';
import type { StandardConfig } from '../shared/types';
import { isLegacyWordFile, LEGACY_WORD_NOT_PROCESSED_MESSAGE } from '../shared/wordFiles';
import { Home } from './pages/Home';
import Projects from './pages/projects/index';
import { SpecLibrary } from './pages/SpecLibrary';
import { ModuleLibrary } from './pages/ModuleLibrary';
// The Designer owns every PROJECT document.
import { Designer, readLastSpecPref } from './designer/Designer';
import { firstSpecUsingForma } from './designer/useDesignerDoc';
// The Master Forma Studio (docs/DESIGNER_UNIFIED_PLAN.md §3 R6) is the
// master forma editor — the hand-built classic canvas retired once parity
// was proven (see docs/rebuild history for the phase-A/B/C reports).
import { Studio } from './forma-studio';
// The Spec Masters Studio (docs/MASTERS_LADDER_PLAN.md §3 R2–R5) — THE masters
// editor. BlockEditor was deleted in R5 once the Studio proved parity against
// docs/reviews/BLOCKEDITOR_INVENTORY.md; every masters entry point lands here.
import { SpecStudio } from './spec-studio/Studio';
// ModulesPage retired in v2.12.0 — library-module transclusion feature removed.
// Forge removed — replaced by Add Specs (master → project) page
import { AddSpecs } from './pages/AddSpecs';
import { OrganizationsPage } from './pages/OrganizationsPage';
import { PeoplePage } from './pages/people/PeoplePage';
// Time & Expense (v2.18 rebuild): Timesheets/Expenses/Vacation tabs.
// Forecasting is its own top-level nav item (D2).
import TimeExpensePage from './pages/time/TimeExpensePage';
import ForecastingPage2 from './pages/forecasting2';
// InvoicesPage removed — duplicate of TimeSheetsPage
import { AdminPage } from './pages/AdminPage';
import {
  computeHarSyncPlan,
  applyHarSyncPlan,
  getHarAutoApply,
  canHarSync,
} from './services/harSyncService';
// Old ReportsPage (raw-table CSV/Excel/PDF dump) is kept on disk but
// no longer imported here — superseded by ReportsPageNew below.
import { TasksKit } from './pages/tasks/TasksKit';
// New invoicing — separate from the legacy timesheet-approval InvoicesPage.tsx,
// which we leave in place but unrouted (slated for cleanup later).
import InvoicesPageNew from './pages/InvoicesPageNew';
// New canonical-reports page — six built-in reports, filters, charts, exports.
// LegacyReportsPage still imported for the simple-export view (CSV/Excel/PDF
// of raw tables) but unrouted now; we may surface it as a "Raw Export" tab
// under the new ReportsPage in a future patch.
import ReportsPageNew from './pages/ReportsPageNew';
import { LoginPage } from './pages/LoginPage';
import { DesktopHandoff, WebClerkAutoAuth, SpecFormaClerkProvider } from './pages/ClerkSignIn';
import { isDesktopSigninRequest } from './lib/clerkConfig';
import { RouterProvider, useBlocker, useLocation, useNavigate } from 'react-router';
import { createAppRouter, stashAddress, takeStashedAddress } from './routing/router';
import { getRecordTitle, subscribeRecordTitle, takeReported } from './routing/addressSync';
import { RecordLink, useRecordMenu } from './routing/RecordLink';
import {
  addressOfState,
  fromAddress,
  surfaceOfParsed,
  surfaceOfState,
  titleFor,
  toAddress,
} from './routing/address';
import { PublicSite } from './marketing/PublicSite';
import { DesktopOnlyNotice } from './components/DesktopOnlyNotice';
import { isWeb } from './lib/platform';
import { pageGate } from './lib/pageGates';
import { AuthService, getAuthToken, signOutLocal } from './services/authService';
import type { AuthUser } from './services/authService';
import { setCurrentFirmId } from './services/firmContext';
import { clearAll, list } from './services/cloudCache';
import { Prefs } from './services/prefsService';
import { useTableSync } from './hooks/useTableSync';
import { FirmPrefs } from './services/firmPrefsService';
import { connectLiveSync, disconnectLiveSync } from './services/liveSync';
import { FormaService } from './services/formaService';
import { SpecService } from './services/specService';
import { ProjectSpecService } from './services/projectSpecService';
import { getAllowedNavIds } from '../shared/modules';
import { hasPermission } from '../shared/permissions';
import { NotificationBell } from './components/NotificationBell';
import { TooltipProvider } from './components/ui/tooltip';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { UpdateDialog } from './components/UpdateDialog';
import { VerifyEmailBanner } from './components/VerifyEmailBanner';
import { Library, Formatter, Settings } from './components/EditorComponents';
import { OrgService } from './services/orgService';
import { reportError } from './services/errorLog';
import type { DeepLink } from './types/navigation';
import { logPageView } from './services/activityLogger';
import { pushEditHistory } from '../shared/editHistory';

/* ─── Sidebar Navigation Icons ─── */
const NAV_ICONS: Record<string, React.ReactNode> = {
  home: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  projects: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  ),
  'spec-masters': (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <line x1="8" y1="7" x2="16" y2="7" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  ),
  modules: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* stacked cubes — reusable building-blocks motif */}
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  formas: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  ),
  compile: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  ),
  format: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  ),
  settings: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09" />
    </svg>
  ),
  sun: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" />
      <line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" />
      <line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  ),
  moon: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  ),
  menu: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
  user: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      style={{ flexShrink: 0 }}
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  back: (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  tbSettings: (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="4" y1="8" x2="20" y2="8" />
      <circle cx="9" cy="8" r="2" />
      <line x1="4" y1="16" x2="20" y2="16" />
      <circle cx="15" cy="16" r="2" />
    </svg>
  ),
  tbExport: (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 3v12" />
      <polyline points="7 10 12 15 17 10" />
      <path d="M5 21h14" />
    </svg>
  ),
  tbSave: (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  tbPencil: (
    <svg
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  ),
  clients: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <circle cx="12" cy="10" r="3" />
      <path d="M8 16c0-2 2-3 4-3s4 1 4 3" />
    </svg>
  ),
  reports: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="12" y2="17" />
      <polyline points="10 9 11 11 14 7" />
    </svg>
  ),
  team: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  'time-expense': (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  forecasting: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="3 17 9 11 13 15 21 6" />
      <polyline points="15 6 21 6 21 12" />
    </svg>
  ),
  invoices: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  ),
  tasks: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  admin: (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
};

const NAV_ITEMS = [
  { id: 'home', label: 'Home' },
  { id: '_section_pm', label: 'Project Management', section: true },
  { id: 'clients', label: 'Clients' },
  { id: 'projects', label: 'Projects' },
  { id: 'team', label: 'People' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'time-expense', label: 'Time & Expense' },
  { id: 'invoices', label: 'Billing' },
  { id: 'forecasting', label: 'Forecasting' },
  { id: 'reports', label: 'Reporting' },
  { id: 'admin', label: 'Admin Center' },
  { id: '_section_spec', label: 'Specifications', section: true },
  { id: 'spec-masters', label: 'Master Specs' },
  // Phase 4 — the firm's reusable block-groups. A MODULE is never a variant.
  { id: 'modules', label: 'Modules' },
  { id: 'formas', label: 'Master Formas' },
  { id: '_section_tools', label: '', section: true },
  { id: 'format', label: 'Formatter' },
  { id: 'settings', label: 'Settings' },
] as const;

const VIEW_TITLES: Record<string, string | null> = {
  editor: null, // dynamic
  home: 'Home',
  clients: 'Clients',
  projects: 'Projects',
  team: 'People',
  tasks: 'Tasks',
  'time-expense': 'Time & Expense',
  invoices: 'Billing',
  forecasting: 'Forecasting',
  admin: 'Admin Center',
  'spec-masters': 'Master Specs',
  // The Studio is a page OF Master Specs; its own header row names the spec.
  'spec-studio': 'Master Specs',
  modules: 'Modules',
  formas: 'Master Formas',
  format: 'Formatter',
  compile: 'Add Specs',
  settings: 'Settings',
};

/* ═══════════ MAIN APP ═══════════ */
function App() {
  // ─── Auth State ───
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const prevFirmIdRef = useRef<string | null>(null);

  // ─── Core State ───
  // D1 cross-surface parity: cloud `user_prefs` + localStorage fast cache.
  // The local seed applies synchronously (before `Prefs` is even ready) so
  // the LOGIN page — pre-auth — still keeps the user's last theme; the
  // cloud value then wins once, the same as every other migrated pref.
  const [themeMode, setThemeMode] = usePref<'light' | 'dark' | 'system'>(
    'sf-theme-mode',
    'sf-theme-mode',
    'system',
    {
      parse: (raw) => (raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'system'),
      serialize: (v) => v,
    }
  );
  const [resolvedDark, setResolvedDark] = useState(
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const [view, setView] = useState('home');
  // Deep-link payload — set by `navigate()` and consumed once by the
  // target page in a useEffect. Pages call `clearDeepLink()` after
  // applying so the same link doesn't re-trigger on re-render.
  const [deepLink, setDeepLink] = useState<DeepLink | null>(null);
  // Addresses (MULTI_WINDOW_PLAN W1): every screen has one address. The
  // router feeds this shell's state; `navigate()` keeps its signature and
  // also pushes the link's address. `lastAddressRef` = the address the shell
  // last wrote/applied, so a location change it caused is never re-applied
  // (no double-consume of a deep link).
  const rrNavigate = useNavigate();
  const rrLocation = useLocation();
  const locAddress = rrLocation.pathname + rrLocation.search;
  const locAddressRef = useRef(locAddress);
  locAddressRef.current = locAddress;
  const lastAddressRef = useRef<string | null>(null);
  const skipOutboundKeyRef = useRef<string | null>(null);
  // Deep-link payload — set by `navigate()` and consumed once by the
  // target page in a useEffect. Pages call `clearDeepLink()` after
  // applying so the same link doesn't re-trigger on re-render.
  const navigate = (link: DeepLink) => {
    setDeepLink(link);
    setView(link.view);
    const addr = toAddress(link);
    lastAddressRef.current = addr;
    if (addr !== locAddressRef.current) void rrNavigate(addr);
  };
  const clearDeepLink = () => setDeepLink(null);

  // Activity log: fire a page.view event whenever the active view
  // changes. Coalesced server-side so re-mounts don't double-count.
  useEffect(() => {
    if (view) logPageView(view);
  }, [view]);
  // ── Sidebar (DESIGN.md §3.6) ──
  // Its width is a FRACTION of the window, not a fixed pixel rail: 200/1500 of
  // the window (exactly 200px on the 1500px reference), floored at 11rem so it
  // never squeezes the labels out and capped at 16rem so it never eats a big
  // screen. The floor/cap follow the AUTO dial ONLY (never the user's
  // Ctrl+=/Settings % factor) — otherwise stepping the Interface-size
  // setting would blow the sidebar wide open along with body text (Josh
  // 2026-09-09). Collapsed is a flat 3.5rem (56px at 100%), unchanged.
  const sidebarWidthPx = React.useCallback(() => {
    const dial = getUiScale().dial;
    return Math.round(
      Math.max(11 * 16 * dial, Math.min((200 / 1500) * window.innerWidth, 16 * 16 * dial))
    );
  }, []);
  const [sbWidth, setSbWidth] = useState(sidebarWidthPx);
  useEffect(() => {
    const onChange = () => setSbWidth(sidebarWidthPx());
    window.addEventListener('resize', onChange);
    const unsubscribe = subscribeUiScale(onChange);
    return () => {
      window.removeEventListener('resize', onChange);
      unsubscribe();
    };
  }, [sidebarWidthPx]);
  // Open/collapsed — D1 cross-surface parity: cloud `user_prefs` +
  // localStorage fast cache (used to reset every launch, then per-device
  // only; now follows the signed-in user across devices too).
  const [sbOpen, setSbOpen] = usePref<boolean>(
    'sf-sidebar-collapsed',
    'sf-sidebar-collapsed',
    true,
    {
      parse: (raw) => raw !== '1',
      serialize: (v) => (v ? '0' : '1'),
    }
  );
  // Kept for the Formatter view's "last active forma" convenience default
  // (EditorComponents.tsx's Formatter, lastActiveId?: string | null) — never
  // set anywhere now that the classic editor is retired, so Formatter simply
  // falls back to settings.defaultTemplateId, same as it always did for a
  // fresh session.
  const [activeId] = useState<string | null>(null);
  // The Master Forma Studio (view === 'forma-studio') — which forma it has open.
  const [studioFormaId, setStudioFormaId] = useState<string | null>(null);
  // A brand-new, not-yet-saved forma (contract §3 R6, item 10a) — set together
  // with studioFormaId=null. CSI-seeded once per "New Forma" click so its
  // object identity stays stable for the life of the draft (re-seeding on
  // every render would blow away whatever the user just typed).
  const [studioNewSeed, setStudioNewSeed] = useState<StandardConfig | null>(null);
  const [standards, setStandards] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>({
    defaultTemplateId: '',
    exportDir: '',
    adminUsernames: ['jbeardsley', 'rfrick', 'dbronson', 'thead'],
  });
  const [toasts, setToasts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState({ username: '', displayName: '', role: 'user' });
  // The Spec Masters Studio (view === 'spec-studio') — which master it has
  // open. R5: the ONLY masters editor; every row click lands here.
  const [studioMasterId, setStudioMasterId] = useState<string | null>(null);
  /** The Studio opens with the publish dialog up (review-queue Approve). */
  const [studioOpenPublish, setStudioOpenPublish] = useState(false);
  const [editorProjectSpecId, setEditorProjectSpecId] = useState<string | null>(null);
  const [editorSourceProjectId, setEditorSourceProjectId] = useState<string | null>(null);
  // The Designer has no modes (docs/DESIGNER_UNIFIED_PLAN.md R5). Opening a
  // PROJECT FORMA still means "I came here to work on the paper", so the
  // workspace brings the formatting tools forward — the forma is styled
  // against a real spec of that project. A fresh ts re-applies the request.
  const [designerFormatIntent, setDesignerFormatIntent] = useState<{ ts: number } | null>(null);
  // Master updates widget → open that section straight into Review.
  const [designerReviewIntent, setDesignerReviewIntent] = useState<{ ts: number } | null>(null);
  // The Specs widget's forma badge: open the project forma straight into Review.
  const [designerFormaReviewIntent, setDesignerFormaReviewIntent] = useState<{
    ts: number;
  } | null>(null);
  const editorDirtyRef = useRef(false);
  // No-work-loss hardening (2026-08-19): mirrors the People page's Org
  // Chart edit-session dirty state so the beforeunload guard below also
  // covers it — a dirty org-chart session used to close/refresh away
  // with zero warning, unlike the spec editor (editorDirtyRef).
  const peopleDirtyRef = useRef(false);
  // The native File-menu Ctrl+S accelerator (main.ts's hidden menu) has no
  // idea what's open — whichever editor is currently mounted (the Studio or
  // the Designer) registers its own save-now trigger here; neither mounted
  // means Ctrl+S is a no-op, which is the correct behavior with no editor
  // open (ENGINEERING.md — preserve every function, never a silent dead key).
  const menuSaveTargetRef = useRef<(() => void) | null>(null);
  const exitGuardTriggerRef = useRef<{ show: () => void } | null>(null);
  const pendingNavRef = useRef<string | null>(null);
  // Mirror editor-open state so the one-shot app-close listener can read the
  // latest value without having to re-subscribe on every state change.
  const studioMasterIdRef = useRef<string | null>(null);
  const editorProjectSpecIdRef = useRef<string | null>(null);
  // editorModuleId / moduleEditorReturnRef removed in v2.12.0 — library
  // module feature retired (see migration 019).
  const [updateInfo, setUpdateInfo] = useState<{
    status: 'checking' | 'up-to-date' | 'available' | 'downloading' | 'ready' | 'error';
    version?: string;
    progress?: number;
    error?: string;
    releaseNotes?: string | null;
  } | null>(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  // "Remind me later" this session — a background download finishing right
  // after must not re-open the modal the user just snoozed (Josh 2026-10-09).
  const remindedThisSessionRef = useRef(false);
  // Multi-window: main-pushed prompts open only in the primary window, or in
  // the window whose user clicked the version badge.
  const askedForUpdateRef = useRef(false);
  const [currentVersion, setCurrentVersion] = useState('');

  useEffect(() => {
    if (window.api?.getAppVersion)
      window.api
        .getAppVersion()
        .then((v: string) => setCurrentVersion(v))
        .catch(() => {});
  }, []);
  // Warn on window close/refresh if editor has unsaved changes
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (editorDirtyRef.current || peopleDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // Block default OS file drag/drop on the window — Electron's default is to
  // navigate the renderer to file:// for the dropped file, which destroys the
  // app state. Component-level drop zones still receive the event because they
  // call stopPropagation before this listener runs.
  useEffect(() => {
    const swallow = (e: DragEvent) => e.preventDefault();
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);
  useEffect(() => {
    studioMasterIdRef.current = studioMasterId;
  }, [studioMasterId]);
  useEffect(() => {
    editorProjectSpecIdRef.current = editorProjectSpecId;
  }, [editorProjectSpecId]);

  // openModuleFromEditor removed in v2.12.0 — library module feature retired.
  // OS close command (title-bar X, Alt+F4, Cmd+Q) — main prevents default and
  // asks us to run the exit-guard flow. If a spec editor is open we raise the
  // same Save / Submit / Discard / Cancel dialog the sidebar uses, marking the
  // pending destination as '__quit__' so the Studio's onBack can route
  // through confirmAppClose() after the user picks an action.
  useEffect(() => {
    if (!window.api?.onAppCloseRequested) return;
    window.api.onAppCloseRequested(() => {
      const editorOpen = !!(studioMasterIdRef.current || editorProjectSpecIdRef.current);
      if (editorOpen && editorDirtyRef.current && exitGuardTriggerRef.current) {
        // Tell main the real exit-guard dialog is up — cancels its close
        // watchdog so this decision is never force-closed out from under
        // the user (Josh 2026-09-25).
        window.api?.notifyCloseGuardShown?.();
        pendingNavRef.current = '__quit__';
        exitGuardTriggerRef.current.show();
      } else {
        window.api?.confirmAppClose?.();
      }
    });
  }, []);
  const [organizations, setOrganizations] = useState<any[]>([]);
  // Projects for the designer's attach-to-project picker (warmed with the cache)
  const [projectsAll, setProjectsAll] = useState<any[]>([]);
  // ─── Toast Helper ───
  // Optional action button (e.g. "Restart now") and a custom lifetime — used
  // by the update-ready toast, which needs longer than the default 3s to be
  // useful.
  const toast = (
    m: string,
    e?: boolean,
    opts?: { action?: { label: string; onClick: () => void }; ms?: number }
  ) => {
    const id = Date.now();
    setToasts((p) => [...p, { id, m, e, action: opts?.action }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), opts?.ms ?? 3000);
  };

  // Open a forma by id: PROJECT forma -> the Designer on a real spec of its project;
  // MASTER forma -> the Studio. Shared by the Projects page and address routing.
  const openFormaById = async (formaId: string, opts?: { review?: boolean }) => {
    // Fresh read — the copy may have been forked seconds ago,
    // before the sync event refreshed `standards`.
    const all = await FormaService.list();
    const s = all.find((f: any) => f.id === formaId);
    if (!s) {
      toast('Forma not found', true);
      return;
    }
    // A PROJECT forma opens in THE DESIGNER's Design mode against
    // a real spec of its project — last opened, else first in CSI
    // order (docs/DESIGNER_PLAN.md R3). A MASTER forma opens in
    // the Studio (contract §3 R6 — parity proven, the classic
    // editor retired).
    if ((s as any).projectId) {
      try {
        const specs = await ProjectSpecService.listByProject((s as any).projectId);
        if (specs.length > 0) {
          let target = specs[0];
          try {
            const lastId = readLastSpecPref((s as any).projectId);
            const last = specs.find((x: any) => x.id === lastId);
            if (last) target = last;
            else {
              const digits = (x: any) => (x.csiNumber || '').replace(/\D/g, '');
              target = [...specs].sort((a: any, b: any) =>
                digits(a) < digits(b) ? -1 : digits(a) > digits(b) ? 1 : 0
              )[0];
            }
          } catch {
            /* localStorage unavailable — first in CSI order */
          }
          // Review: open a spec that PRINTS with this forma, or
          // its Review cannot open there.
          if (opts?.review) {
            const using = firstSpecUsingForma(all, specs, (s as any).projectId, formaId);
            if (using) target = using;
            else {
              target = specs[0];
              toast(
                `No spec in this project uses “${
                  (s as any).docSettings?.standardName || 'this forma'
                }” — opening the first spec`
              );
            }
          }
          setDesignerFormatIntent({ ts: Date.now() });
          setDesignerReviewIntent(null);
          setDesignerFormaReviewIntent(opts?.review ? { ts: Date.now() } : null);
          setEditorSourceProjectId((s as any).projectId);
          setEditorProjectSpecId(target.id);
          return;
        }
        toast('This project has no specs yet — opening the forma editor');
      } catch (err) {
        console.warn('[designer] spec lookup failed, using forma editor:', err);
      }
    }
    setStudioNewSeed(null);
    setStudioFormaId((s as any).id);
    setView('forma-studio');
  };

  // ─── Address routing ───
  const shellKey = [
    view,
    studioMasterId,
    editorProjectSpecId,
    studioFormaId,
    editorSourceProjectId,
  ].join('|');

  // Inbound: a location change (Back/Forward, typed address, new window,
  // legacy task-email link) lands in the EXISTING deep-link consumer path.
  useEffect(() => {
    if (!authUser) {
      lastAddressRef.current = null;
      // Desktop sign-in handoff: remember what this window was opened for.
      if (!isWeb) stashAddress(locAddress);
      return;
    }
    if (lastAddressRef.current === null) {
      const stashed = takeStashedAddress();
      if (!isWeb && locAddress === '/' && stashed) {
        void rrNavigate(stashed, { replace: true });
        return;
      }
    }
    if (lastAddressRef.current === locAddress) return;
    // A page wrote this address itself (it opened the record): it already
    // shows it — never re-consume it as a deep link.
    if (takeReported(locAddress)) {
      lastAddressRef.current = locAddress;
      return;
    }
    lastAddressRef.current = locAddress;
    skipOutboundKeyRef.current = shellKey;
    const parsed = fromAddress(locAddress);
    const closeEditors = () => {
      setStudioMasterId(null);
      setEditorProjectSpecId(null);
      setStudioFormaId(null);
    };
    switch (parsed.kind) {
      case 'redirect':
        lastAddressRef.current = null;
        void rrNavigate(parsed.to, { replace: true });
        return;
      case 'unknown':
        toast('That page does not exist', true);
        closeEditors();
        setView('home');
        lastAddressRef.current = '/';
        void rrNavigate('/', { replace: true });
        return;
      case 'view':
        closeEditors();
        setView(parsed.view);
        return;
      case 'module':
        closeEditors();
        setView('modules');
        return;
      case 'client':
        closeEditors();
        setView('clients');
        return;
      case 'link':
        closeEditors();
        setDeepLink(parsed.link);
        setView(parsed.link.view);
        return;
      case 'spec-studio':
        setStudioOpenPublish(false);
        setEditorProjectSpecId(null);
        setStudioFormaId(null);
        setStudioMasterId(parsed.masterId);
        setView('spec-studio');
        return;
      case 'forma-studio':
        setStudioNewSeed(null);
        setStudioMasterId(null);
        setEditorProjectSpecId(null);
        setStudioFormaId(parsed.formaId);
        setView('forma-studio');
        return;
      case 'project-spec':
        setStudioMasterId(null);
        setStudioFormaId(null);
        setDesignerFormatIntent(parsed.review === 'forma' ? { ts: Date.now() } : null);
        setDesignerReviewIntent(parsed.review === 'master' ? { ts: Date.now() } : null);
        setDesignerFormaReviewIntent(parsed.review === 'forma' ? { ts: Date.now() } : null);
        setEditorSourceProjectId(parsed.projectId);
        setEditorProjectSpecId(parsed.specId);
        setView('projects');
        return;
      case 'project-forma':
        closeEditors();
        setView('projects');
        void openFormaById(parsed.formaId);
        return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locAddress, authUser]);

  // Outbound: when the shell itself moves (sidebar, Open spec, Back to
  // list), push the matching address. Skipped while the state has not yet
  // moved off what an inbound address started from.
  useEffect(() => {
    if (!authUser || lastAddressRef.current === null) return;
    if (skipOutboundKeyRef.current !== null) {
      if (skipOutboundKeyRef.current === shellKey) return;
      skipOutboundKeyRef.current = null;
    }
    const cur = fromAddress(locAddressRef.current);
    const state = { view, studioMasterId, editorProjectSpecId, studioFormaId };
    if (surfaceOfState(state) === surfaceOfParsed(cur)) return;
    const next = addressOfState(state, editorSourceProjectId);
    if (!next || next === locAddressRef.current) return;
    lastAddressRef.current = next;
    void rrNavigate(next, { replace: cur.kind === 'project-forma' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shellKey, authUser]);

  // Exit guard for ADDRESS-driven moves (browser Back/Forward, typed address):
  // the same Save / Discard prompt or People toast the sidebar road shows. A
  // move the shell itself made (lastAddressRef already holds it) is never
  // blocked; nothing is prompted unless a dirty ref is set.
  const blockedNavRef = useRef<{ proceed: () => void; reset: () => void } | null>(null);
  const settleBlockedNav = (go: boolean) => {
    const b = blockedNavRef.current;
    blockedNavRef.current = null;
    if (b) (go ? b.proceed : b.reset)();
  };
  const guardStateRef = useRef({ view, studioMasterId, editorProjectSpecId, studioFormaId });
  guardStateRef.current = { view, studioMasterId, editorProjectSpecId, studioFormaId };
  const addrBlocker = useBlocker(({ nextLocation }) => {
    if (!peopleDirtyRef.current && !editorDirtyRef.current) return false;
    const nextAddr = nextLocation.pathname + nextLocation.search;
    if (nextAddr === lastAddressRef.current) return false;
    const parsed = fromAddress(nextAddr);
    if (parsed.kind === 'redirect' || parsed.kind === 'unknown') return false;
    return surfaceOfState(guardStateRef.current) !== surfaceOfParsed(parsed);
  });
  useEffect(() => {
    if (addrBlocker.state !== 'blocked') return;
    const { proceed, reset } = addrBlocker;
    const parsed = fromAddress(addrBlocker.location.pathname + addrBlocker.location.search);
    const targetView =
      parsed.kind === 'view'
        ? parsed.view
        : parsed.kind === 'link'
          ? parsed.link.view
          : parsed.kind === 'module'
            ? 'modules'
            : parsed.kind === 'client'
              ? 'clients'
              : parsed.kind === 'spec-studio'
                ? 'spec-studio'
                : parsed.kind === 'forma-studio'
                  ? 'forma-studio'
                  : 'projects';
    const st = guardStateRef.current;
    if (st.view === 'team' && targetView !== 'team' && peopleDirtyRef.current) {
      toast('Save or cancel your Org Chart changes before switching pages', true);
      reset();
      return;
    }
    const editorOpen =
      (st.studioMasterId && st.view === 'spec-studio') ||
      (st.editorProjectSpecId && st.view === 'projects');
    if (editorOpen && editorDirtyRef.current && exitGuardTriggerRef.current) {
      blockedNavRef.current = { proceed, reset };
      pendingNavRef.current = targetView;
      exitGuardTriggerRef.current.show();
      return;
    }
    proceed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addrBlocker.state]);

  // Tab title: "<record> · <parent> · SpecForma" from names already loaded.
  const recordTitle = React.useSyncExternalStore(subscribeRecordTitle, getRecordTitle);
  const sidebarMenu = useRecordMenu();
  useEffect(() => {
    const parsed = fromAddress(locAddress);
    const projectId =
      parsed.kind === 'link' && parsed.link.view === 'projects'
        ? parsed.link.projectId
        : parsed.kind === 'project-spec' || parsed.kind === 'project-forma'
          ? parsed.projectId
          : undefined;
    const p = projectId ? projectsAll.find((x: any) => x.id === projectId) : undefined;
    const project: string | undefined = p ? p.projectNumber || p.name || undefined : undefined;
    document.title = titleFor(parsed, { project, record: recordTitle ?? undefined });
  }, [locAddress, projectsAll, recordTitle]);

  // Restart-to-update: shared by the UpdateDialog's "Restart & update"
  // button and the sidebar pill (the update-ready toast was removed
  // 2026-10-09 — the must-answer modal carries this now). Main pre-flights
  // the installer file; {ok:false} means it vanished — heal by downloading
  // again instead of leaving the user stuck.
  const handleInstallUpdate = async () => {
    const res = await window.api?.installUpdate?.();
    if (res && (res as any).cancelled) {
      setShowUpdateDialog(false);
      toast('Update postponed: another window has unsaved work.', true);
      return;
    }
    if (res && (res as any).ok === false) {
      setUpdateInfo((prev) => ({ ...prev, status: 'downloading', progress: 0 }) as any);
      window.api?.downloadUpdate?.();
    }
  };

  // UpdateDialog's three must-answer actions (Josh 2026-10-09).
  const handleUpdateNow = () => {
    if (updateInfo?.status === 'ready') {
      handleInstallUpdate();
      return;
    }
    setUpdateInfo((prev) => ({ ...prev, status: 'downloading', progress: 0 }) as any);
    window.api?.downloadUpdate?.();
  };
  const handleRemindLater = () => {
    window.api?.remindUpdateLater?.();
    remindedThisSessionRef.current = true;
    setShowUpdateDialog(false);
  };
  const handleSkipVersion = () => {
    const v = updateInfo?.version;
    if (v) window.api?.skipUpdateVersion?.(v);
    setShowUpdateDialog(false);
    setUpdateInfo(null);
  };
  const handleRetryCheck = () => {
    setUpdateInfo({ status: 'checking' });
    window.api?.checkForUpdates?.();
  };

  // ─── Theme ───
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setResolvedDark(e.matches);
    mq.addEventListener('change', handler);
    setResolvedDark(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const isDark = themeMode === 'system' ? resolvedDark : themeMode === 'dark';
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  // App-level Ctrl+scroll zoom REMOVED (uniformity): the UI is responsive at 100%
  // and only the document canvas zooms (like Word). One layout, one set of ratios.
  // A stale saved zoom from the old system is cleared so nobody is stuck scaled.
  useEffect(() => {
    try {
      localStorage.removeItem('sf-app-zoom');
    } catch {}
  }, []);

  // ─── Auto-Updater Listener ───
  useEffect(() => {
    if (!window.api) return;
    setUpdateInfo({ status: 'checking' });
    // Main's per-window `prompt` flag is the only gate: the primary role can
    // move when the first window closes, so a mount-time ask would go stale.
    const mayPrompt = () => true;
    window.api.onUpdateAvailable((_e: any, info: any) => {
      // 'available', NOT 'downloading' (2026-08-14): a MANUAL check is
      // consensual — main does not start a download, it waits for the
      // Download click. Jumping to 'downloading' here left the dialog
      // frozen at 0% forever (nothing was downloading; the 'available'
      // Download-button state was unreachable). Background checks DO
      // auto-download, and their first progress event flips this to
      // 'downloading' within seconds, so both paths stay truthful.
      // Functional form (fix 2026-10-09b): a replay/un-skip send can omit
      // version — keep whatever we already had rather than blanking the
      // dialog's "v…" text.
      setUpdateInfo(
        (prev) =>
          ({
            status: 'available',
            version: info?.version ?? prev?.version,
            releaseNotes: info?.releaseNotes ?? prev?.releaseNotes,
          }) as any
      );
      // Must-answer modal (Josh 2026-10-09): main decides prompt — never for
      // a skipped version in the background, and never while snoozed.
      if (info?.prompt && mayPrompt()) setShowUpdateDialog(true);
    });
    window.api.onUpdateDownloadProgress((_e: any, info: any) => {
      setUpdateInfo(
        (prev) => ({ ...prev, status: 'downloading', progress: Math.round(info.percent) }) as any
      );
    });
    window.api.onUpdateDownloaded((_e: any, info: any) => {
      // Must-answer modal (Josh 2026-10-09, supersedes the 2026-09-25
      // non-blocking ruling): once a download is ready, main's `prompt`
      // flag opens the Update-now/Remind-me-later/Don't-tell-me-again
      // dialog — unless this session already said "remind me later" for it,
      // or the version was skipped/snoozed. The sidebar pill still carries
      // the quiet state either way.
      setUpdateInfo(
        (prev) => ({ ...prev, status: 'ready', version: info?.version ?? prev?.version }) as any
      );
      if (info?.prompt && !remindedThisSessionRef.current && mayPrompt()) setShowUpdateDialog(true);
    });
    window.api.onUpdateUpToDate(() => {
      setUpdateInfo({ status: 'up-to-date' });
    });
    window.api.onUpdateError((_e: any, info: any) => {
      const msg = info?.message || 'Unable to check for updates';
      // Keep version/notes so a retry or a late progress event still names it.
      setUpdateInfo((prev) => ({ ...prev, status: 'error', error: msg }) as any);
      // A background download can error (stall watchdog, network drop) while
      // the dialog was never open — surface it as a toast too, or the failure
      // is invisible (2026-08-13: this is the "frozen at 0%" report's real
      // dead end; the corner badge now also renders the error state below).
      toast(msg, true);
    });
  }, []);

  // Update-ready toast REMOVED (Josh 2026-10-09): the must-answer modal
  // (UpdateDialog) now prompts directly once a download is ready, and the
  // persistent sidebar pill still carries the quiet state — a toast on top
  // of a modal was redundant.

  // ─── Set firm context + pre-warm cache (only invalidate on firm change) ───
  useEffect(() => {
    const newFirmId = authUser?.firmId || null;
    setCurrentFirmId(newFirmId);

    if (newFirmId) {
      // Connect live sync WebSocket for real-time updates
      connectLiveSync();

      // Push enabled modules to main process for IPC enforcement
      if (authUser!.enabledModules) {
        window.api.setFirmModules(authUser!.enabledModules).catch(() => {});
      }
      // Switch to firm-scoped file directories (specs/formas live on disk)
      window.api
        .switchFirmDb(newFirmId)
        .catch((e: unknown) => console.error('Failed to switch firm context:', e));

      // Only clear + re-warm when firmId actually changes
      if (prevFirmIdRef.current !== newFirmId) {
        prevFirmIdRef.current = newFirmId;
        clearAll();
        Prefs.reset();
        FirmPrefs.reset();
        setDataReady(false);
        // Pre-warm critical tables — fetch in small batches to avoid overwhelming the API
        const tables = [
          'team_members',
          'organizations',
          'projects',
          'roles',
          'billing_roles',
          'project_types',
          'billing_types',
          'activity_codes',
          'default_phases',
          'project_scopes',
          'teams',
          'team_assignments',
          'project_assignments',
          'overhead_categories',
          'expense_types',
          'tasks',
          'invitations',
          'standard_configs',
          'departments',
          'project_budgets',
          'dashboard_views',
        ];
        (async () => {
          // Batch in groups of 8 — server handles concurrent requests fine
          for (let i = 0; i < tables.length; i += 8) {
            const batch = tables.slice(i, i + 4);
            const results = await Promise.allSettled(batch.map((t) => list(t)));
            results.forEach((r, j) => {
              if (r.status === 'rejected')
                console.warn(`[app] Failed to load ${batch[j]}:`, r.reason?.message);
              else
                console.log(
                  `[app] Loaded ${batch[j]}: ${(r as PromiseFulfilledResult<any[]>).value.length} rows`
                );
            });
          }
          // Load formas after cache is warmed (firm context is set)
          try {
            const stds = await FormaService.list();
            setStandards(stds || []);
          } catch (err) {
            console.warn('[app] Failed to load formas:', err);
          }
          try {
            const orgs = await OrgService.list();
            setOrganizations(orgs);
          } catch (err) {
            reportError('app:loadOrgs', err);
          }
          try {
            const projs = await list('projects');
            setProjectsAll(projs || []);
          } catch (err) {
            reportError('app:loadProjects', err);
          }
          // Per-user UI prefs (table layouts) — synced across devices/web/
          // desktop. Fire-and-forget: DataTable applies them when they arrive;
          // boot must never wait on (or fail with) the prefs fetch.
          if (authUser?.id) void Prefs.load(authUser.id);
          // Firm-level UI defaults (workspace layouts a colleague published
          // for everyone who never customised — contract §2.9). Same
          // fire-and-forget rule: a firm with none simply uses out-of-box.
          if (newFirmId) void FirmPrefs.load(newFirmId);
          setDataReady(true);
        })();
      }
    } else {
      if (prevFirmIdRef.current !== null) {
        prevFirmIdRef.current = null;
        clearAll();
        Prefs.reset();
        FirmPrefs.reset();
        setDataReady(false);
      }
    }
  }, [authUser]);

  // ─── Auth: check saved JWT on mount, show login if none ───
  useEffect(() => {
    let authSub: { unsubscribe: () => void } | null = null;
    (async () => {
      const timeout = setTimeout(() => setAuthLoading(false), 5000);
      try {
        const u = await AuthService.getUser();
        setAuthUser(u);
        setAuthLoading(false);
        clearTimeout(timeout);
      } catch (e) {
        console.error('Auth check failed:', e);
        setAuthLoading(false);
        clearTimeout(timeout);
      }

      const {
        data: { subscription },
      } = AuthService.onAuthChange((u) => {
        // Only update auth user — firm context effect handles cache if firmId changes.
        // Do NOT invalidate cache here: token refreshes are routine and should be invisible.
        setAuthUser(u);
      });
      authSub = subscription;
    })();
    return () => {
      authSub?.unsubscribe();
    };
  }, []);

  // Re-fetch the auth user whenever the window regains focus or the role/team-member
  // tables change. This is how role edits (and module/tier changes) propagate to a
  // signed-in user without forcing them to sign out and back in. The /auth/me call
  // is cheap and the listener is scoped so it only fires when something relevant
  // actually changes, not on every cloud sync.
  useEffect(() => {
    if (!authUser) return;
    const refresh = async () => {
      try {
        const u = await AuthService.getUser();
        if (u) setAuthUser(u);
      } catch (err) {
        reportError('app:refreshAuthUser', err);
      }
    };
    const onSync = (e: Event) => {
      const tables: string[] = (e as CustomEvent).detail?.tables || [];
      if (tables.includes('roles') || tables.includes('team_members')) refresh();
    };
    window.addEventListener('focus', refresh);
    window.addEventListener('specforma-sync-complete', onSync);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('specforma-sync-complete', onSync);
    };
  }, [authUser?.id]);

  // Another window or tab signed out: drop this one's session too.
  useEffect(() => {
    const off = window.api.onAuthSignedOut?.(() => {
      if (!getAuthToken()) return;
      disconnectLiveSync();
      signOutLocal();
      setAuthUser(null);
    });
    return () => off?.();
  }, []);

  // Freshness across windows: a second window's pref change (theme, sidebar,
  // layouts, saved views) and forma-library edits arrive on the /ws push.
  // Prefs.refetch keeps keys with a pending local save; it only invalidates,
  // so it cannot re-fire this event.
  useTableSync(['user_prefs'], () => {
    void Prefs.refetch();
  });
  useTableSync(['standard_configs'], () => {
    FormaService.list()
      .then((stds) => setStandards(stds || []))
      .catch((err) => reportError('app:refreshFormas', err));
  });

  // (Realtime subscriptions removed — local PostgreSQL, no cloud sync)

  // ─── HAR Sync (RWB bridge): once per sign-in, after the cache pre-warm ───
  // Gated to Josh's account + desktop (canHarSync). Ref-keyed on userId so
  // window-focus /auth/me refreshes don't re-trigger it. Silent when no
  // workbook source is configured on this machine (plan comes back null).
  // WRITE-SAFE: the startup pass only COMPUTES the plan and notifies; it
  // writes nothing unless the explicit auto-apply pref is on.
  const harSyncRanForUser = React.useRef<string | null>(null);
  useEffect(() => {
    if (!dataReady || !authUser) return;
    if (!canHarSync(authUser)) return;
    if (harSyncRanForUser.current === authUser.id) return;
    harSyncRanForUser.current = authUser.id;
    (async () => {
      try {
        const plan = await computeHarSyncPlan();
        if (!plan || plan.total === 0) return;
        if (await getHarAutoApply()) {
          const res = await applyHarSyncPlan(plan);
          toast(
            `HAR sync applied: +${res.projectsAdded} projects, +${res.deliverablesAdded} deliverables, ` +
              `+${res.assignmentsAdded} assignments, ${res.forecastCellsSet} forecast cells`
          );
        } else {
          toast(
            `HAR sync: ${plan.total} pending additions — review in Admin Center › Data › HAR Sync`
          );
        }
      } catch (err) {
        console.warn('[harSync] startup check failed:', err);
      }
    })();
  }, [dataReady, authUser?.id]);

  useEffect(() => {
    async function init() {
      try {
        if (window.api) {
          const s = await window.api.loadSettings();
          if (s) setSettings(s);
          if (window.api.getCurrentUser) {
            const user = await window.api.getCurrentUser();
            if (user) setCurrentUser(user);
          }
        }
      } catch (err) {
        console.error('Init error:', err);
      }
      setLoading(false);
    }
    init();
  }, []);

  // Native File-menu accelerators (main.ts's hidden menu, Ctrl/Cmd+N and
  // +S) — forwarded over IPC since the menu itself has no idea what's on
  // screen. New always opens a fresh CSI-seeded forma in the Studio
  // (unconditional, same as the classic editor's Ctrl+N always did — "new
  // forma" isn't tied to what happens to be open). Save targets whichever
  // editor most recently registered a save-now trigger (the Studio or the
  // Designer, via registerSaveHandler); neither open, Ctrl+S is a no-op —
  // there is genuinely nothing to save.
  const handleNewStudioRef = useRef<() => void>(() => {});
  useEffect(() => {
    if (!window.api) return;
    const onNew = () => handleNewStudioRef.current();
    const onSave = () => menuSaveTargetRef.current?.();
    window.api.onMenuNew(onNew);
    window.api.onMenuSave(onSave);
    return () => {
      if (window.api?.removeMenuListeners) window.api.removeMenuListeners();
    };
  }, []);

  // "New Forma" from the Formas library opens the Studio, CSI-seeded
  // (contract §3 R6, item 10a).
  const handleNewStudio = () => {
    const seed: StandardConfig = {
      id: String(Date.now()),
      docSettings: mkDoc(),
      levels: csiLevelStyles(),
      headerFooter: mkHF(),
      special: mkSp(),
    };
    setStudioNewSeed(seed);
    setStudioFormaId(null);
    setView('forma-studio');
  };
  handleNewStudioRef.current = handleNewStudio;

  // "Import" from the Formas library opens the Studio with the harvested
  // draft (contract §3 R6 — same as the Studio's own Import… toolbar
  // action, useMasterFormaDraft.importDraft handles the rest once it
  // mounts). Gap-filled to CSI SectionFormat defaults, same as any import.
  const handleImportStudio = async () => {
    if (!window.api) {
      toast('Import requires desktop app', true);
      return;
    }
    const filePath = await window.api.openDotxDialog();
    if (!filePath) return;
    let importPath = filePath;
    let convertedPath: string | null = null;
    if (isLegacyWordFile(filePath)) {
      if (!window.api.convertWordToDocx) {
        toast(LEGACY_WORD_NOT_PROCESSED_MESSAGE, true);
        return;
      }
      try {
        const conv = await window.api.convertWordToDocx(filePath);
        if (!conv.ok) {
          toast(conv.message, true);
          return;
        }
        importPath = conv.docxPath;
        convertedPath = conv.docxPath;
      } catch (err: any) {
        toast('Import error: ' + err.message, true);
        return;
      }
    }
    try {
      const result = await window.api.importTemplate(importPath);
      if (!result.success || !result.config) {
        toast('Import failed: ' + (result.error || 'Unknown error'), true);
        return;
      }
      setStudioNewSeed(fillFormaCsiGaps(result.config) as StandardConfig);
      setStudioFormaId(null);
      setView('forma-studio');
      toast('Imported — review and Save to add it to your Master Formas');
    } catch (err: any) {
      toast('Import error: ' + err.message, true);
    } finally {
      if (convertedPath) void window.api.cleanupConvertedDocx?.(convertedPath);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await FormaService.delete(id);
      setStandards((p) => p.filter((s: any) => s.id !== id));
      toast('Deleted');
    } catch (err) {
      toast('Delete failed', true);
    }
  };

  const handleArchiveForma = async (ids: string[], archived: boolean) => {
    if (!ids.length) return;
    try {
      const who = authUser?.displayName || currentUser.displayName || 'Unknown';
      for (const id of ids) await FormaService.setArchived(id, archived, who);
      const nowIso = new Date().toISOString();
      setStandards((p) =>
        p.map((s: any) =>
          ids.includes(s.id)
            ? { ...s, archivedAt: archived ? nowIso : null, archivedBy: archived ? who : null }
            : s
        )
      );
      toast(
        `${archived ? 'Archived' : 'Restored'} ${ids.length} forma${ids.length === 1 ? '' : 's'}`
      );
    } catch {
      toast(`Could not ${archived ? 'archive' : 'restore'} those formas`, true);
    }
  };

  const handleDup = async (id: string) => {
    const s = standards.find((x: any) => x.id === id);
    if (!s) return;
    const n = { ...JSON.parse(JSON.stringify(s)), id: String(Date.now()) };
    delete n.draftDirty; // a copy of the PUBLISHED forma starts with no pending draft
    if (n.docSettings) n.docSettings.standardName += ' (Copy)';
    const dupUser = authUser?.displayName || currentUser.displayName || 'Unknown';
    n.editHistory = pushEditHistory([], dupUser, authUser?.id, 'duplicated');
    try {
      await FormaService.save(n);
      setStandards((p) => [...p, n]);
      toast('Duplicated');
    } catch {
      toast('Duplicate failed', true);
    }
  };

  const handleSettingsChange = async (s: any) => {
    setSettings(s);
    if (window.api) {
      await window.api.saveSettings(s);
    }
  };

  // ─── Derived ───
  const viewTitle = VIEW_TITLES[view] || '';

  const handleSignOut = async () => {
    disconnectLiveSync();
    await AuthService.signOut();
    setAuthUser(null);
  };

  // ─── Render ───

  // Auth loading spinner — tokens come from base.css (design-system layer)
  if (authLoading || (authUser && !dataReady))
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: 'var(--bg)',
          color: 'var(--tm)',
        }}
      >
        {authUser ? 'Loading data...' : 'Loading...'}
      </div>
    );

  // Desktop update dialog — defined BEFORE the auth gate so it renders on
  // the login screen too: updating must never require signing in. Must-answer
  // modal (Josh 2026-10-09, supersedes the 2026-09-25 non-blocking ruling):
  // Update now / Remind me later / Don't tell me again — built on the kit
  // Dialog (see src/renderer/components/UpdateDialog.tsx).
  const updateDialogEl =
    !isWeb && updateInfo ? (
      <UpdateDialog
        open={showUpdateDialog}
        onOpenChange={setShowUpdateDialog}
        status={updateInfo.status}
        version={updateInfo.version}
        currentVersion={currentVersion}
        progress={updateInfo.progress}
        error={updateInfo.error}
        releaseNotes={updateInfo.releaseNotes}
        onUpdateNow={handleUpdateNow}
        onRemindLater={handleRemindLater}
        onSkipVersion={handleSkipVersion}
        onRetry={handleRetryCheck}
      />
    ) : null;

  // Desktop browser-sign-in handoff (docs/AUTH_CLERK_PLAN.md): the exe opens
  // specforma.app/?desktop-signin=1 — that request renders ONLY the handoff
  // page (works whether or not a web session already exists).
  {
    const isWebBrowser = typeof navigator !== 'undefined' && !/electron/i.test(navigator.userAgent);
    if (isWebBrowser && isDesktopSigninRequest()) {
      return <DesktopHandoff />;
    }
  }

  // Logged-out entry. On the web (specforma.app) show the public marketing site
  // (Procore/SpecLink model: one domain = site + app); the desktop app goes
  // straight to the login screen, exactly as before (parity preserved).
  if (!authUser) {
    const isWebBrowser = typeof navigator !== 'undefined' && !/electron/i.test(navigator.userAgent);
    return isWebBrowser ? (
      <SpecFormaClerkProvider>
        {/* Clerk reloads the page after sign-in; this silent bridge finishes
            the Clerk→SpecForma exchange on any web load with a Clerk session. */}
        <WebClerkAutoAuth onAuth={setAuthUser} />
        <PublicSite onAuth={setAuthUser} />
      </SpecFormaClerkProvider>
    ) : (
      <>
        <LoginPage onAuth={setAuthUser} />
        {updateDialogEl}
      </>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        background: 'var(--bg)',
        color: 'var(--tx)',
      }}
    >
      <style>{`
      @keyframes si{from{transform:translateY(-16px);opacity:0}to{transform:translateY(0);opacity:1}}
    `}</style>

      {/* ── Sidebar ── */}
      <div
        style={{
          width: sbOpen ? sbWidth : rem(56),
          minWidth: sbOpen ? sbWidth : rem(56),
          background: 'var(--sb)',
          color: 'var(--st)',
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid var(--sbd)',
          transition: 'all .25s',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: rem(14),
            display: 'flex',
            alignItems: 'center',
            gap: rem(10),
            borderBottom: '1px solid var(--sbd)',
            minHeight: rem(50),
            justifyContent: sbOpen ? 'flex-start' : 'center',
          }}
        >
          <div
            style={{ cursor: 'pointer', flexShrink: 0, display: 'flex' }}
            onClick={() => setSbOpen(!sbOpen)}
          >
            {NAV_ICONS.menu}
          </div>
          {sbOpen && (
            <span
              style={{
                fontSize: 'var(--font-size-md)',
                fontWeight: 700,
                letterSpacing: '-.02em',
                whiteSpace: 'nowrap',
              }}
            >
              SpecForma™
            </span>
          )}
        </div>
        <div
          style={{
            flex: 1,
            padding: 'var(--space-2) 0',
            display: 'flex',
            flexDirection: 'column',
            gap: rem(2),
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {(() => {
            const moduleNavIds = authUser?.enabledModules
              ? getAllowedNavIds(authUser.enabledModules)
              : new Set(NAV_ITEMS.map((n: any) => n.id));
            return NAV_ITEMS.filter((n: any) => {
              // Web: the Formatter (local Word engine) is desktop-only — no nav
              // entry. Deep links to 'format' still land on DesktopOnlyNotice.
              if (isWeb && n.id === 'format') return false;
              // Module-based access: hide pages for disabled modules
              if (!n.section && !moduleNavIds.has(n.id)) return false;
              // Section headers: hide if all children are filtered out
              if (n.section) {
                const sectionIdx = NAV_ITEMS.indexOf(n);
                const nextSectionIdx = NAV_ITEMS.findIndex(
                  (item: any, i: number) => i > sectionIdx && item.section
                );
                const children = NAV_ITEMS.slice(
                  sectionIdx + 1,
                  nextSectionIdx === -1 ? undefined : nextSectionIdx
                );
                const hasVisibleChild = children.some(
                  (c: any) => !c.section && moduleNavIds.has(c.id)
                );
                if (!hasVisibleChild) return false;
              }
              // Role-based access: use granular permissions
              const up = authUser?.permissions || [];
              if (n.id === 'admin' && !up.includes('admin_center.view')) return false;
              if (n.id === 'format' && !up.includes('formatter.view')) return false;
              // Phase 4 — the module library has its own permission section.
              if (n.id === 'modules' && !up.includes('modules.view')) return false;
              if (
                n.id === 'reports' &&
                !up.includes('reports.view') &&
                !up.includes('admin_center.view')
              )
                return false;
              // Project Canvas R1 (T3b): these four gates are the shared
              // `pageGate` predicate (src/renderer/lib/pageGates.ts) — same
              // keys, same `includes` semantics as before extraction — so
              // the project dashboard's nested-tool host can never drift
              // from what the sidebar itself allows (Decision 13).
              if (n.id === 'time-expense' && !pageGate('time-expense', up)) return false;
              // D2: Forecasting's own nav item — same visibility rule the
              // old Time & Expense tab used (any forecasting view/edit key;
              // with none, the scoped grid has nothing to show anyone).
              if (n.id === 'forecasting' && !pageGate('forecasting', up)) return false;
              if (n.id === 'team' && !pageGate('team', up)) return false;
              if (n.id === 'invoices' && !pageGate('invoices', up)) return false;
              // Tasks Kit Decision 16 — same shared gate ProjectToolHost's
              // nested Tasks door and the Tasks widget's expand/drills use.
              if (n.id === 'tasks' && !pageGate('tasks', up)) return false;
              return true;
            });
          })().map((n: any) => {
            if (n.section) {
              if (!n.label)
                return (
                  <div
                    key={n.id}
                    style={{
                      margin: 'var(--space-1) var(--space-3)',
                      borderTop: '1px solid var(--sbd)',
                    }}
                  />
                );
              return sbOpen ? (
                <div
                  key={n.id}
                  style={{
                    padding: `${rem(10)} var(--space-4) ${rem(2)}`,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 700,
                    color: 'var(--st)',
                    opacity: 0.4,
                    letterSpacing: '.08em',
                    textTransform: 'uppercase',
                    userSelect: 'none',
                  }}
                >
                  {n.label}
                </div>
              ) : (
                <div
                  key={n.id}
                  style={{ margin: `${rem(6)} var(--space-3)`, borderTop: '1px solid var(--sbd)' }}
                />
              );
            }
            const openNavItem = () => {
              // No-work-loss hardening (2026-08-19): an active Org Chart
              // edit session with unsaved changes used to be silently
              // discarded by any left-nav click away from People — block
              // it the same way a dirty session blocks the Directory/Org
              // Chart tab switch inside PeoplePage itself.
              if (view === 'team' && n.id !== 'team' && peopleDirtyRef.current) {
                toast('Save or cancel your Org Chart changes before switching pages', true);
                return;
              }
              // Spec editor is open AND has actual unsaved edits → force
              // Save / Discard / Submit choice. If the editor is open but
              // clean (just opened to view), let the user navigate away
              // silently — no nag. (v2.11.11 — was unconditional in v2.5+.)
              const editorOpen =
                (studioMasterId && view === 'spec-studio') ||
                (editorProjectSpecId && view === 'projects');
              if (editorOpen && editorDirtyRef.current) {
                pendingNavRef.current = n.id;
                if (exitGuardTriggerRef.current) {
                  exitGuardTriggerRef.current.show();
                  return;
                }
              }
              setView(n.id);
              if (n.id !== 'spec-studio') setStudioMasterId(null);
              if (n.id !== 'projects') setEditorProjectSpecId(null);
            };
            return (
              <RecordLink
                key={n.id}
                href={toAddress(n.id)}
                draggable={false}
                onOpen={openNavItem}
                onContextMenu={(e) => {
                  sidebarMenu.show(e, toAddress(n.id), openNavItem);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: rem(10),
                  padding: sbOpen ? `${rem(10)} var(--space-4)` : `${rem(10)} 0`,
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-base)',
                  borderRadius: 'var(--radius-md)',
                  margin: sbOpen ? '0 var(--space-2)' : '0 var(--space-1)',
                  whiteSpace: 'nowrap',
                  background: view === n.id ? 'var(--ach)' : 'transparent',
                  color: view === n.id ? 'var(--ac)' : 'var(--st)',
                  fontWeight: view === n.id ? 600 : 400,
                  transition: 'all .15s',
                  justifyContent: sbOpen ? 'flex-start' : 'center',
                }}
              >
                <span style={{ flexShrink: 0, display: 'flex' }}>{NAV_ICONS[n.id]}</span>
                {sbOpen && <span>{n.label}</span>}
              </RecordLink>
            );
          })}
          {sidebarMenu.menu}
        </div>
        {authUser && (
          <div style={{ borderTop: '1px solid var(--sbd)' }}>
            <NotificationBell
              memberId={authUser.id}
              collapsed={!sbOpen}
              onNavigateTask={(taskId) => navigate({ view: 'tasks', taskId })}
            />
          </div>
        )}
        <div
          style={{
            borderTop: '1px solid var(--sbd)',
            display: 'flex',
            flexDirection: sbOpen ? 'row' : 'column',
            alignItems: 'center',
          }}
        >
          <div
            onClick={() =>
              setThemeMode((m) => (m === 'light' ? 'system' : m === 'system' ? 'dark' : 'light'))
            }
            style={{
              padding: 'var(--space-3) var(--space-4)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              cursor: 'pointer',
              fontSize: 'calc(var(--font-size-base) * 12 / 13)',
              flex: 1,
              minWidth: 0,
              justifyContent: sbOpen ? 'flex-start' : 'center',
            }}
          >
            {isDark ? NAV_ICONS.sun : NAV_ICONS.moon}
            {sbOpen && (
              <span>
                {themeMode === 'system' ? 'System' : themeMode === 'dark' ? 'Dark' : 'Light'}
              </span>
            )}
          </div>
          {authUser && (
            <div
              style={{
                padding: sbOpen ? 'var(--space-3) var(--space-4) var(--space-3) 0' : `${rem(6)} 0`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: rem(6),
                flexShrink: 0,
              }}
              title="Connected to cloud"
            >
              <span
                style={{
                  width: rem(7),
                  height: rem(7),
                  borderRadius: '50%',
                  flexShrink: 0,
                  background: '#5cb85c',
                }}
              />
              {sbOpen && (
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)' }}>Cloud</span>
              )}
            </div>
          )}
        </div>
        {currentVersion && (
          <div
            onClick={() => {
              // Web: version badge is display-only — updates ship with the site.
              if (isWeb) return;
              askedForUpdateRef.current = true;
              setShowUpdateDialog(true);
              if (window.api?.checkForUpdates) {
                setUpdateInfo({ status: 'checking' });
                window.api.checkForUpdates();
              }
            }}
            style={{
              padding: sbOpen ? 'var(--space-2) var(--space-4)' : 'var(--space-2) 0',
              fontSize: 'var(--font-size-sm)',
              color: updateInfo?.status === 'ready' ? 'var(--ac)' : 'var(--tm)',
              cursor: isWeb ? 'default' : 'pointer',
              fontFamily: "'JetBrains Mono', monospace",
              borderTop: '1px solid var(--sbd)',
              display: 'flex',
              alignItems: 'center',
              gap: rem(6),
              whiteSpace: 'nowrap',
              justifyContent: sbOpen ? 'flex-start' : 'center',
            }}
            title={isWeb ? undefined : 'Check for updates'}
          >
            {sbOpen && <span>v{currentVersion}</span>}
            {updateInfo?.status === 'available' && (
              <span
                style={{
                  fontSize: 'calc(var(--font-size-base) * 9 / 13)',
                  fontWeight: 700,
                  padding: `${rem(1)} ${rem(5)}`,
                  borderRadius: rem(8),
                  background: '#f59e0b',
                  color: '#fff',
                  lineHeight: 1.2,
                  letterSpacing: '.02em',
                }}
              >
                {sbOpen ? 'UPDATE' : '!'}
              </span>
            )}
            {updateInfo?.status === 'downloading' && (
              <span
                style={{
                  fontSize: 'calc(var(--font-size-base) * 9 / 13)',
                  fontWeight: 600,
                  color: 'var(--ac)',
                }}
              >
                {updateInfo.progress || 0}%
              </span>
            )}
            {updateInfo?.status === 'ready' && (
              <span
                onClick={(e) => {
                  // Persistent pill (Josh 2026-09-25): clicking it installs
                  // directly instead of reopening the manual check-for-updates
                  // flow the rest of the footer row triggers.
                  e.stopPropagation();
                  handleInstallUpdate();
                }}
                title="Update ready — restart now"
                style={{
                  fontSize: 'calc(var(--font-size-base) * 9 / 13)',
                  fontWeight: 700,
                  padding: `${rem(1)} ${rem(5)}`,
                  borderRadius: rem(8),
                  background: 'var(--ac)',
                  color: '#fff',
                  lineHeight: 1.2,
                }}
              >
                {sbOpen ? 'RESTART' : '!'}
              </span>
            )}
            {updateInfo?.status === 'error' && (
              <span
                style={{
                  fontSize: 'calc(var(--font-size-base) * 9 / 13)',
                  fontWeight: 700,
                  padding: `${rem(1)} ${rem(5)}`,
                  borderRadius: rem(8),
                  background: '#dc3c3c',
                  color: '#fff',
                  lineHeight: 1.2,
                }}
                title="Update failed — click to retry"
              >
                {sbOpen ? 'RETRY' : '!'}
              </span>
            )}
            {!sbOpen && !updateInfo?.status && (
              <span
                style={{ fontSize: 'calc(var(--font-size-base) * 9 / 13)', color: 'var(--tm)' }}
              >
                v{currentVersion.split('.').slice(0, 2).join('.')}
              </span>
            )}
          </div>
        )}
        {authUser && (
          <div
            style={{
              padding: `${rem(10)} var(--space-4)`,
              borderTop: '1px solid var(--sbd)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              fontSize: 'var(--font-size-sm)',
              justifyContent: sbOpen ? 'flex-start' : 'center',
            }}
          >
            {NAV_ICONS.user}
            {sbOpen && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: rem(2),
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    fontWeight: 600,
                    color: 'var(--st)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {authUser.displayName}
                </span>
                <span
                  style={{ cursor: 'pointer', color: 'var(--tm)', fontSize: 'var(--font-size-xs)' }}
                  onClick={handleSignOut}
                >
                  Sign out
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Main Content ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {authUser && (
          <VerifyEmailBanner authUser={authUser} onVerified={() => window.location.reload()} />
        )}
        {/* Top bar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: `var(--space-2) ${rem(20)}`,
            background: 'var(--hd)',
            borderBottom: '1px solid var(--bd)',
            minHeight: rem(50),
            gap: 'var(--space-3)',
          }}
        >
          <div style={{ fontSize: 'calc(var(--font-size-base) * 15 / 13)', fontWeight: 600 }}>
            {viewTitle}
          </div>
        </div>

        {/* Page Content */}
        <div
          style={{
            flex: 1,
            overflow:
              view === 'spec-masters' ||
              view === 'spec-studio' ||
              view === 'compile' ||
              view === 'clients' ||
              view === 'projects'
                ? 'hidden'
                : 'auto',
          }}
        >
          <ErrorBoundary resetKey={view} title="This page hit an error">
            {view === 'home' && <Home onNavigate={(v) => setView(v)} toast={toast} />}
            {view === 'clients' && <OrganizationsPage toast={toast} authUser={authUser} />}
            {view === 'projects' && !editorProjectSpecId && (
              <Projects
                toast={toast}
                authUser={authUser}
                onOpenProjectSpec={(
                  specId: string,
                  projectId: string,
                  opts?: { review?: boolean }
                ) => {
                  setDesignerFormatIntent(null);
                  setDesignerFormaReviewIntent(null);
                  setDesignerReviewIntent(opts?.review ? { ts: Date.now() } : null);
                  setEditorProjectSpecId(specId);
                  setEditorSourceProjectId(projectId);
                }}
                onOpenForma={openFormaById}
                initialProjectId={editorSourceProjectId || undefined}
                navigate={navigate}
                deepLink={deepLink && deepLink.view === 'projects' ? deepLink : null}
                onDeepLinkConsumed={clearDeepLink}
              />
            )}
            {/* Project specs open in THE DESIGNER; masters open in the Studio.
              (docs/DESIGNER_PLAN.md R1, MASTERS_LADDER_PLAN §R5.) */}
            {view === 'projects' && editorProjectSpecId && (
              <Designer
                projectSpecId={editorProjectSpecId}
                onOpenSection={(specId: string) => setEditorProjectSpecId(specId)}
                onManageSections={() => {
                  setDesignerReviewIntent(null);
                  setDesignerFormaReviewIntent(null);
                  setEditorProjectSpecId(null);
                }}
                onBack={() => {
                  editorDirtyRef.current = false;
                  setDesignerReviewIntent(null);
                  setDesignerFormaReviewIntent(null);
                  setEditorProjectSpecId(null);
                  if (pendingNavRef.current === '__quit__') {
                    pendingNavRef.current = null;
                    window.api?.confirmAppClose?.();
                    return;
                  }
                  if (pendingNavRef.current) {
                    setEditorSourceProjectId(null);
                    setView(pendingNavRef.current);
                    pendingNavRef.current = null;
                  }
                }}
                toast={toast}
                userName={authUser?.displayName || 'user'}
                canEdit={
                  hasPermission(authUser?.permissions || [], 'project_specs.edit_any') ||
                  hasPermission(authUser?.permissions || [], 'project_specs.edit_own')
                }
                canViewModules={hasPermission(authUser?.permissions || [], 'modules.view')}
                isDark={isDark}
                userId={authUser?.id}
                openFormatting={designerFormatIntent}
                openReview={designerReviewIntent}
                registerSaveHandler={(fn) => {
                  menuSaveTargetRef.current = fn;
                }}
                canIssue={hasPermission(
                  authUser?.permissions || [],
                  'project_specs.create_issuance'
                )}
                canEditProject={hasPermission(authUser?.permissions || [], 'projects.edit')}
                canSetFirmDefault={hasPermission(
                  authUser?.permissions || [],
                  'admin_center.manage_firm_settings'
                )}
                canViewMasterUpdates={hasPermission(
                  authUser?.permissions || [],
                  'project_specs.view'
                )}
                canPublish={hasPermission(authUser?.permissions || [], 'specs.publish')}
                canAcceptMasterUpdates={hasPermission(authUser?.permissions || [], 'specs.publish')}
                openFormaReview={designerFormaReviewIntent}
                canEditForma={hasPermission(authUser?.permissions || [], 'formas.edit')}
                canPublishForma={hasPermission(authUser?.permissions || [], 'formas.publish')}
              />
            )}
            {view === 'team' && (
              <PeoplePage
                toast={toast}
                authUser={authUser}
                deepLink={deepLink && deepLink.view === 'team' ? deepLink : null}
                onDeepLinkConsumed={clearDeepLink}
                onDirtyChange={(d) => {
                  peopleDirtyRef.current = d;
                }}
              />
            )}
            {view === 'tasks' && (
              <TasksKit
                toast={toast}
                authUser={authUser}
                deepLink={deepLink && deepLink.view === 'tasks' ? deepLink : null}
                onDeepLinkConsumed={clearDeepLink}
              />
            )}
            {view === 'time-expense' && (
              <TimeExpensePage
                toast={toast}
                authUser={authUser}
                deepLink={deepLink && deepLink.view === 'time-expense' ? deepLink : null}
                onDeepLinkConsumed={clearDeepLink}
              />
            )}
            {view === 'forecasting' && (
              <ForecastingPage2
                toast={toast}
                authUser={authUser}
                deepLink={deepLink && deepLink.view === 'forecasting' ? deepLink : null}
                onDeepLinkConsumed={clearDeepLink}
              />
            )}

            {view === 'invoices' && (
              <InvoicesPageNew
                toast={toast}
                authUser={authUser}
                deepLink={deepLink && deepLink.view === 'invoices' ? deepLink : null}
                onDeepLinkConsumed={clearDeepLink}
              />
            )}
            {view === 'reports' && <ReportsPageNew toast={toast} authUser={authUser} />}
            {view === 'admin' && (
              <AdminPage
                toast={toast}
                userPermissions={authUser?.permissions}
                userEmail={authUser?.email}
                enabledModules={authUser?.enabledModules}
              />
            )}
            {view === 'formas' && (
              <Library
                standards={standards.filter((s: any) => !s.projectId)}
                defaultId={settings.defaultTemplateId}
                // Every master forma opens in the Studio (contract §3 R6 —
                // parity proven, the classic editor retired).
                onLoad={(s: any) => {
                  setStudioNewSeed(null);
                  setStudioFormaId(s.id);
                  setView('forma-studio');
                }}
                onCreate={handleNewStudio}
                onImport={handleImportStudio}
                onDup={handleDup}
                onDel={handleDelete}
                onArchive={handleArchiveForma}
                userPermissions={authUser?.permissions}
              />
            )}

            {view === 'forma-studio' && (studioFormaId || studioNewSeed) && (
              <Studio
                formaId={studioFormaId}
                newForma={studioNewSeed}
                onBack={() => {
                  setStudioFormaId(null);
                  setStudioNewSeed(null);
                  setView('formas');
                }}
                organizations={organizations}
                projects={projectsAll}
                onFormaPersisted={(f: any) =>
                  setStandards((p) => {
                    const x = p.findIndex((s: any) => s.id === f.id);
                    if (x >= 0) {
                      const n = [...p];
                      n[x] = f;
                      return n;
                    }
                    return [...p, f];
                  })
                }
                registerSaveHandler={(fn) => {
                  menuSaveTargetRef.current = fn;
                }}
                canSetFirmDefault={hasPermission(
                  authUser?.permissions || [],
                  'admin_center.manage_firm_settings'
                )}
                toast={toast}
                isDark={isDark}
                userId={authUser?.id}
                userName={authUser?.displayName || currentUser.displayName || 'Unknown'}
                userPermissions={authUser?.permissions}
              />
            )}

            {view === 'format' &&
              (isWeb ? (
                <DesktopOnlyNotice />
              ) : (
                <Formatter
                  standards={standards.filter((s: any) => !s.projectId)}
                  lastActiveId={activeId}
                  settings={settings}
                  onSettingsChange={handleSettingsChange}
                  userPermissions={authUser?.permissions}
                  toast={toast}
                />
              ))}
            {view === 'spec-masters' && (
              <SpecLibrary
                onOpenEditor={(id: string, opts?: { openPublish?: boolean }) => {
                  // R5: every masters entry point opens THE STUDIO. BlockEditor
                  // is gone; there is one masters editor.
                  setStudioOpenPublish(!!opts?.openPublish);
                  setStudioMasterId(id);
                  setView('spec-studio');
                }}
                toast={toast}
                userRole={authUser?.role || 'member'}
                userPermissions={authUser?.permissions || []}
                userName={authUser?.displayName || 'user'}
              />
            )}
            {/* The Module Library (Phase 4) — firm-wide reusable block-groups.
              Read-only preview; placing a module lands in Phase 5. */}
            {view === 'modules' && (
              <ModuleLibrary
                toast={toast}
                userName={authUser?.displayName || 'user'}
                userPermissions={authUser?.permissions || []}
                userId={authUser?.id}
                isDark={isDark}
              />
            )}
            {/* The Spec Masters Studio (Masters Ladder R2–R5) — THE masters
              editor. Every entry point in the app lands here. */}
            {view === 'spec-studio' && studioMasterId && (
              <SpecStudio
                masterId={studioMasterId}
                openPublish={studioOpenPublish}
                onPublishOpened={() => setStudioOpenPublish(false)}
                onBack={() => {
                  editorDirtyRef.current = false;
                  setStudioMasterId(null);
                  if (pendingNavRef.current === '__quit__') {
                    pendingNavRef.current = null;
                    window.api?.confirmAppClose?.();
                    return;
                  }
                  if (pendingNavRef.current) {
                    setView(pendingNavRef.current);
                    pendingNavRef.current = null;
                    settleBlockedNav(true);
                    return;
                  }
                  setView('spec-masters');
                }}
                toast={toast}
                userPermissions={authUser?.permissions || []}
                userName={authUser?.displayName || 'user'}
                userId={authUser?.id}
                isDark={isDark}
                onSavedAs={(newId) => {
                  setStudioOpenPublish(false);
                  setStudioMasterId(newId);
                }}
                parentDirtyRef={editorDirtyRef}
                exitGuardTriggerRef={exitGuardTriggerRef}
                onCancelExit={() => {
                  if (pendingNavRef.current === '__quit__') window.api?.notifyCloseCancelled?.();
                  pendingNavRef.current = null;
                  settleBlockedNav(false);
                }}
                registerSaveHandler={(fn) => {
                  menuSaveTargetRef.current = fn;
                }}
              />
            )}
            {view === 'compile' && (
              <AddSpecs
                toast={toast}
                standards={standards}
                deepLink={deepLink && deepLink.view === 'compile' ? deepLink : null}
                onDeepLinkConsumed={clearDeepLink}
                onExit={(projectId?: string) => {
                  if (projectId) setEditorSourceProjectId(projectId);
                  setView('projects');
                }}
                onOpenProjectDashboard={(projectId: string) => {
                  setEditorSourceProjectId(projectId);
                  setView('projects');
                }}
                userPermissions={authUser?.permissions}
              />
            )}
            {view === 'settings' && (
              <Settings
                themeMode={themeMode}
                onThemeMode={(m: string) => setThemeMode(m as 'light' | 'dark' | 'system')}
                standards={standards}
                settings={settings}
                onSettingsChange={handleSettingsChange}
                currentUser={currentUser}
                userPermissions={authUser?.permissions}
              />
            )}
          </ErrorBoundary>
        </div>
      </div>

      {updateDialogEl}

      {/* ── Toasts ── */}
      <div
        style={{
          position: 'fixed',
          top: rem(16),
          right: rem(16),
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)',
        }}
      >
        {toasts.map((t: any) => (
          <div
            key={t.id}
            style={{
              padding: `${rem(10)} ${rem(20)}`,
              borderRadius: rem(8),
              fontSize: 'var(--font-size-base)',
              fontWeight: 500,
              color: '#fff',
              background: t.e ? 'var(--dg)' : 'var(--ac)',
              boxShadow: '0 4px 16px rgba(0,0,0,.2)',
              animation: 'si .3s',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
            }}
          >
            <span>{t.m}</span>
            {t.action && (
              <button
                onClick={t.action.onClick}
                style={{
                  padding: `${rem(4)} ${rem(10)}`,
                  borderRadius: rem(6),
                  border: '1px solid rgba(255,255,255,.5)',
                  background: 'transparent',
                  color: '#fff',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Mount ───
// One TooltipProvider for the whole app so <Hint> works anywhere (including
// the login/marketing early-return branches). 250ms is Radix's "explain on
// hover, don't nag" default band — fast enough to feel instant on purpose,
// slow enough that sweeping the mouse across a palette shows nothing.
const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(
  <TooltipProvider delayDuration={250} skipDelayDuration={400}>
    <RouterProvider router={createAppRouter(<App />)} />
  </TooltipProvider>
);
